import BaseInteraction from './BaseInteraction';
import ContentItem from '../api/content/ContentItem';
import attachBitmovinEvents from './bitmovin/attachBitmovinEvents';
import cleanse from '../config/util/cleanse';
import logger from '../log';
import {
  BitmovinOptions,
  BitmovinPlayer,
  BitmovinSource,
  ParsedItem
} from '@kollective-frontend/ksdk-api';
import { SourceType } from '../common/constants';
import { checkType, Types } from '../util/checkTypes';
import { getSourceTypeForMime } from '../common/utils/sourceTypeUtils';
import setupBitmovinPlayer from './bitmovin/setupBitmovinPlayer';
import { TechApi } from '../bootloader/tech';

const log = logger.getLogger(__filename);

// These are all the source elements that we don't update.
const SOURCE_ELEMENTS = [
  'analytics',
  'description',
  'drm',
  'labeling',
  'options',
  'poster',
  'subtitleTracks',
  'thumbnailTrack',
  'title'
];
const MEDIA_SOURCES = ['dash', 'hls', 'progressive', 'smooth'];

type RSU = Record<string, unknown>;
type RSAA = Record<string, string[]>;
// Get only the elements we don't change.
export const toLimitedSource = (source: BitmovinSource): BitmovinSource => {
  const src = source as unknown as RSU;
  return SOURCE_ELEMENTS.reduce((acc: RSU, val: string) => {
    acc[val] = src[val];
    return acc;
  }, {} as RSU) as unknown as BitmovinSource;
};

export const parseItem = (item: ContentItem): ParsedItem => {
  const { data, description, title, url } = item;
  const { mime } = data || {};
  const sourceType = getSourceTypeForMime(mime, url);
  switch (sourceType) {
    case SourceType.SMOOTH:
      return { description, title, name: 'smooth', value: url };
    case SourceType.DASH:
      return { description, title, name: 'dash', value: url };
    case SourceType.HLS:
      return { description, title, name: 'hls', value: url };
    case SourceType.MP4:
      return { description, title, name: 'progressive', value: url };
  }
  log.debug('unable to parse item:', item);
  return {};
};

class BitmovinInteraction extends BaseInteraction<BitmovinPlayer> {
  container: HTMLElement;
  source: BitmovinSource;

  constructor(options: BitmovinOptions, tech: TechApi) {
    // Don't have the player at start.
    super({} as unknown as BitmovinPlayer, tech);
    this.container = options.container;
    this.source = options.source || {};
    this.player = setupBitmovinPlayer(options, this.binder);
    checkType(options, Types.BitmovinOptions).emitErrorIfNotCorrectType();
  }

  loader(items: ContentItem[], isFiltered?: boolean): ContentItem[] {
    const limitedSource = toLimitedSource(this.source);
    const source = items.reduce(
      (acc, item) => {
        const { description, name, title, value } = parseItem(item);
        if (name && value) {
          (acc as unknown as Record<string, string>)[name] = value;
          // Choose first description and title if not aleady set.
          if (!acc.description && description) {
            acc.description = description;
          }
          if (!acc.title && title) {
            acc.title = title;
          }
        }
        return acc;
      },
      isFiltered ? limitedSource : this.source
    );
    if (isFiltered) {
      this.logBadSources(
        source as unknown as RSU,
        this.source as unknown as RSU
      );
    }
    this.setSource(source);
    // We filter our the `this.source` above as it might contain types we cannot play.
    // thus it is safe to return this list.
    return items;
  }

  logBadSources(source: RSU, fullSource: RSU): RSAA {
    const errs = MEDIA_SOURCES.reduce(
      (acc: RSAA, key: string) => {
        if (!fullSource[key]) {
          return acc;
        } else if (!source[key]) {
          // key is in the original sources but not ours
          acc.removed.push(key);
        } else if (source[key] !== fullSource[key]) {
          acc.modified.push(key);
        }
        return acc;
      },
      { modified: [], removed: [] }
    );
    if (errs.modified.length > 0) {
      // This is to be expected for 10 agents.
      log.debug(`Modified the following sources: ${errs.modified}`);
    }
    if (errs.removed.length > 0) {
      log.warn(`Removed: ${errs.removed} as they could cause peering to fail`);
    }
    return errs;
  }

  addEventListeners(): void {
    const result = checkType(this.player, Types.BITMOVIN_ATTACH);
    if (result.isType) {
      const { player, tech } = this;
      this.cleanupMethods.push(attachBitmovinEvents({ player, tech }));
    } else {
      result.log(log, log.ERROR, 'Unable to add event listeners');
    }
  }

  setSource(source: BitmovinSource): void {
    const result = checkType(this.player, Types.BITMOVIN_SET_SOURCE);
    if (!result.isType) {
      result.log(log, log.ERROR, 'Unable to run player.load to set source');
      return;
    }
    // TODO: add event watching...
    const csource = cleanse(source);
    log.info('Setting source to:', { source: csource, player: this.player });
    this.player.load(csource).then(
      (...success: unknown[]) => {
        const v = { success, player: this.player };
        log.info('Successfully created Bitmovin Player instance', v);
      },
      (reason: unknown) => {
        log.info('Error while loading Bitmovin Player instance', { reason });
      }
    );
  }

  clearSource(): void {
    log.warn(this.generatePlannedErrorMessage('clearSource', 'Bitmovin'));
  }

  mediaElement(): HTMLMediaElement | undefined {
    if (typeof this.player?.getVideoElement !== 'function') {
      log.info('mediaElement(): player is not correctly configured');
      return undefined;
    }
    return this.player.getVideoElement();
  }

  getAllowedSourceTypes(): SourceType[] {
    return [SourceType.DASH, SourceType.HLS, SourceType.SMOOTH];
  }
}
export default BitmovinInteraction;
